import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Configuration for the ConfigurableReuseStrategy
 * Allows specifying which routes should be reused and which should always be recreated
 */
export interface RouteReuseConfig {
  /** Routes that should never be reused (always recreated) */
  neverReuseRoutes: string[];
  
  /** Routes that should always be reused when possible */
  alwaysReuseRoutes: string[];
  
  /** Maximum number of detached routes to store in memory */
  maxDetachedRoutes: number;
}

/**
 * A configurable route reuse strategy that allows fine-grained control over which routes are reused
 * This helps with memory management while still allowing performance optimizations for specific routes
 */
export class ConfigurableReuseStrategy implements RouteReuseStrategy {
  /** Map of stored route handles */
  private storedRoutes = new Map<string, DetachedRouteHandle>();
  
  /** Default configuration */
  private defaultConfig: RouteReuseConfig = {
    neverReuseRoutes: [],
    alwaysReuseRoutes: [],
    maxDetachedRoutes: 5
  };
  
  /** Current configuration */
  private config: RouteReuseConfig;

  /**
   * Creates a new instance of ConfigurableReuseStrategy
   * @param config Optional configuration to override defaults
   */
  constructor(config?: Partial<RouteReuseConfig>) {
    this.config = { ...this.defaultConfig, ...config };
  }

  /**
   * Updates the strategy configuration
   * @param config New configuration to apply
   */
  public updateConfig(config: Partial<RouteReuseConfig>): void {
    this.config = { ...this.config, ...config };
    
    // If max detached routes decreased, remove excess stored routes
    if (this.storedRoutes.size > this.config.maxDetachedRoutes) {
      const routesToRemove = this.storedRoutes.size - this.config.maxDetachedRoutes;
      const keys = Array.from(this.storedRoutes.keys());
      
      for (let i = 0; i < routesToRemove; i++) {
        this.storedRoutes.delete(keys[i]);
      }
    }
  }

  /**
   * Gets the path from a route
   * @param route The route to get the path from
   * @returns The full path of the route
   */
  private getRoutePath(route: ActivatedRouteSnapshot): string {
    if (route.routeConfig && route.routeConfig.path) {
      return route.routeConfig.path;
    }
    return '';
  }

  /**
   * Determines if a route should be detached when navigating away
   * @param route The route being navigated away from
   * @returns True if the route should be stored for later reuse
   */
  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const path = this.getRoutePath(route);
    
    // Never detach routes that are configured to never be reused
    if (this.config.neverReuseRoutes.includes(path)) {
      return false;
    }
    
    // Always detach routes that are configured to always be reused
    if (this.config.alwaysReuseRoutes.includes(path)) {
      return true;
    }
    
    // Default behavior: don't detach
    return false;
  }

  /**
   * Stores a detached route
   * @param route The route being detached
   * @param handle The detached route handle
   */
  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) return;
    
    const path = this.getRoutePath(route);
    if (!path) return;
    
    // If we've reached the maximum number of stored routes and this is a new route,
    // remove the oldest route before adding this one
    if (this.storedRoutes.size >= this.config.maxDetachedRoutes && !this.storedRoutes.has(path)) {
      const oldestKey = this.storedRoutes.keys().next().value;
      if (oldestKey) {
        this.storedRoutes.delete(oldestKey);
      }
    }
    
    this.storedRoutes.set(path, handle);
  }

  /**
   * Determines if a route should be reattached when navigating to it
   * @param route The route being navigated to
   * @returns True if there is a stored route that should be reused
   */
  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const path = this.getRoutePath(route);
    
    // If the route is configured to never be reused, don't reattach
    if (this.config.neverReuseRoutes.includes(path)) {
      return false;
    }
    
    // Check if we have this route stored
    return !!path && this.storedRoutes.has(path);
  }

  /**
   * Retrieves a previously detached route
   * @param route The route to retrieve
   * @returns The stored route handle, or null if not found
   */
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const path = this.getRoutePath(route);
    if (!path) return null;
    
    return this.storedRoutes.get(path) || null;
  }

  /**
   * Determines if a route should be reused
   * @param future The new route
   * @param curr The current route
   * @returns True if the route should be reused
   */
  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Always reuse if it's the exact same route configuration
    if (future.routeConfig === curr.routeConfig) {
      return true;
    }
    
    // Get paths
    const futurePath = this.getRoutePath(future);
    const currPath = this.getRoutePath(curr);
    
    // If either route is configured to never be reused, don't reuse
    if (
      (futurePath && this.config.neverReuseRoutes.includes(futurePath)) ||
      (currPath && this.config.neverReuseRoutes.includes(currPath))
    ) {
      return false;
    }
    
    // If both routes are configured to always be reused, reuse them
    if (
      futurePath && 
      currPath && 
      this.config.alwaysReuseRoutes.includes(futurePath) && 
      this.config.alwaysReuseRoutes.includes(currPath)
    ) {
      return true;
    }
    
    // Default behavior: only reuse if it's the exact same route
    return false;
  }

  /**
   * Clears all stored routes
   * Useful when logging out or when you want to force fresh route creation
   */
  public clearStoredRoutes(): void {
    this.storedRoutes.clear();
  }
}
